import { Plugin, TextSelection } from 'prosemirror-state';
import { Fragment } from 'prosemirror-model';

/**
 * A mensagem como uma lista de LINHAS, que e como o WhatsApp a guarda.
 *
 * A documentacao da Meta e explicita: o corpo de uma mensagem e uma string de
 * texto puro, e a formatacao sao marcadores dentro dela. Nao existe bloco, nao
 * existe paragrafo, e `\n` e conteudo igual a qualquer letra. Por isso "quantas
 * linhas em branco" nunca foi problema la.
 *
 * A aplicacao guardava o oposto: um formato de DOCUMENTO (CommonMark), onde a
 * linha em branco entre dois paragrafos e estrutura e nao conteudo. Uma, duas
 * ou tres linhas em branco eram gravadas identicas e voltavam como uma so —
 * nao por escolha, mas porque o `\n\n` do CommonMark diz "ha separador", nunca
 * "ha quantos".
 *
 * Aqui as linhas de texto corrido passam a viver DENTRO de um paragrafo, como
 * `hard_break`. Duas quebras seguidas sao uma linha em branco, tres sao duas, e
 * assim por diante — e o serializador as grava com barra invertida, que
 * sobrevive a ida e volta (ver `hardBreakComBarra`).
 *
 * O paragrafo volta a ser o que ele e de fato: fronteira de BLOCO. Ele so
 * aparece em volta de lista, bloco de codigo e citacao, onde a quebra e
 * estrutura mesmo.
 *
 * Duas coisas dependem disto e moram longe:
 *
 *   - o `Enter` insere uma quebra, e nao um paragrafo novo (ver `keymap.js`).
 *     Com paragrafo, o Enter no meio de um texto somava DUAS linhas em branco,
 *     porque a fronteira ja vale uma;
 *   - a entrega desfaz a barra invertida (ver `normalizeBlockBreaks`).
 */

const ehParagrafo = node => node.type.name === 'paragraph';

/**
 * Onde ha dois paragrafos colados, que precisam virar um.
 *
 * Olha apenas os filhos diretos do `doc`: paragrafo dentro de item de lista e
 * conteudo do item, e junta-los ali destruiria a lista.
 *
 * @param {Node} doc
 * @returns {number[]} o offset do SEGUNDO paragrafo de cada par, em ordem de
 *   documento; quem aplica percorre de tras para frente, senao a primeira
 *   juncao desloca a posicao das seguintes.
 */
export function juncoesDeParagrafo(doc) {
  const filhos = [];
  doc.forEach((node, offset) => filhos.push({ node, offset }));

  return filhos
    .slice(0, -1)
    .map(({ node }, indice) => ({ node, proximo: filhos[indice + 1] }))
    .filter(
      ({ node, proximo }) => ehParagrafo(node) && ehParagrafo(proximo.node)
    )
    .map(({ proximo }) => proximo.offset);
}

/**
 * O par de quebras que substitui a fronteira entre dois paragrafos.
 *
 * Sao SEMPRE duas: a fronteira vale uma linha em branco, e a primeira fecha a
 * linha de cima enquanto a segunda abre a linha vazia.
 *
 * Ja foi uma so quando a transacao vinha de desligar uma lista, para que ligar
 * e desligar fosse ida e volta. Nao da: `P(texto) + lista + P(fim)` tem a mesma
 * forma quer a lista tenha nascido de um corte nesta sessao, quer tenha vindo
 * do conteudo gravado — e no segundo caso juntar com uma quebra APAGA linhas em
 * branco que o autor escreveu e nunca tocou. Abrir uma campanha salva e tirar a
 * lista colava os tres blocos na parede de texto que este PR desfaz.
 *
 * O preco e que ligar e desligar a lista deixa as linhas em branco que a lista
 * abriu. O autor VE isso na tela o tempo todo — a lista e desenhada com o vao
 * em volta —, e apagar conteudo em silencio e bem pior do que deixar visivel o
 * que a fronteira significa.
 *
 * @param {Schema} schema
 * @returns {Node[]}
 */
const parDeQuebras = schema => [
  schema.nodes.hard_break.create(),
  schema.nodes.hard_break.create(),
];

/**
 * Junta os paragrafos colados numa transacao, preservando a selecao.
 *
 * @param {EditorState} state
 * @param {Transaction} tr
 * @returns {boolean} true quando juntou alguma coisa
 */
function juntarNaTransacao(state, tr) {
  const juncoes = juncoesDeParagrafo(state.doc);

  if (!juncoes.length) return false;

  const cursor = state.selection.from;

  [...juncoes].reverse().forEach(offset => {
    // As quebras entram no FIM do paragrafo de cima — uma posicao antes da
    // fronteira, que e onde o conteudo dele termina.
    tr.insert(offset - 1, Fragment.from(parDeQuebras(state.schema)));

    // As duas quebras empurraram a fronteira duas posicoes para a frente.
    tr.join(offset + 2);
  });

  // Com o cursor parado EM CIMA da juncao — digitar no fim do paragrafo de
  // cima —, o mapeamento padrao o joga para depois das quebras recem-postas, e
  // ele descia duas linhas sozinho. O vies -1 o mantem antes delas, colado no
  // caractere que acabou de ser escrito.
  if (state.selection.empty) {
    tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map(cursor, -1)));
  }

  return true;
}

/**
 * Poe o documento na forma de linhas, para montar o estado inicial.
 *
 * O markdown gravado ainda traz paragrafo — tanto o conteudo antigo quanto
 * qualquer bloco de verdade —, entao e aqui que o texto corrido vira linhas ao
 * abrir a tela. Sem isto o editor abriria com um paragrafo por bloco e o Enter
 * passaria a misturar as duas representacoes.
 *
 * @param {Node} doc
 * @param {Schema} schema
 * @returns {Node}
 */
export function comoLinhas(doc, schema) {
  if (!schema.nodes.hard_break) return doc;

  const filhos = [];
  doc.forEach(node => filhos.push(node));

  const juntados = filhos.reduce((acc, node) => {
    const anterior = acc[acc.length - 1];

    if (!anterior || !ehParagrafo(anterior) || !ehParagrafo(node)) {
      acc.push(node);
      return acc;
    }

    acc[acc.length - 1] = anterior.copy(
      anterior.content
        .append(Fragment.from(parDeQuebras(schema)))
        .append(node.content)
    );

    return acc;
  }, []);

  return doc.type.create(doc.attrs, Fragment.fromArray(juntados), doc.marks);
}

/**
 * Mantem a forma de linhas enquanto se digita.
 *
 * Roda em `appendTransaction` porque paragrafo colado nasce por muitos
 * caminhos — colar markdown, desfazer, arrastar, sair de uma lista. A
 * transacao devolvida ja esta na forma final, entao a chamada seguinte nao
 * acha nada e nao ha risco de laco.
 *
 * @param {Schema} schema
 * @returns {Plugin}
 */
export function blankLinePlugin(schema) {
  return new Plugin({
    appendTransaction: (transactions, oldState, newState) => {
      if (!schema.nodes.hard_break) return null;
      if (!transactions.some(tr => tr.docChanged)) return null;

      const tr = newState.tr;

      return juntarNaTransacao(newState, tr) ? tr : null;
    },
  });
}

export default blankLinePlugin;
